import styled from 'styled-components';

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay, rgba(0, 0, 0, 0.5));
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

export const ModalContent = styled.div`
  background: var(--surface-color, var(--background-primary));
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-lg);
  position: relative;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--control-radius, var(--card-radius));
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  min-width: 44px;
  min-height: 44px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-primary);
      background: var(--background-secondary);
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--focus-ring);
  }
`;

export const Title = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

export const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0 0 2rem 0;
  text-align: center;
  line-height: 1.5;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Label = styled.label`
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const Icon = styled.div`
  position: absolute;
  left: 12px;
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 12px 12px 12px 40px;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--control-radius, var(--card-radius));
  transition: color 0.2s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-secondary);
    }
  }
`;

export const Button = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 48px;
  width: 100%;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

export const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.08);
  color: rgba(220, 53, 69, 0.95);
  padding: 0.75rem;
  border-radius: var(--card-radius);
  font-size: 0.875rem;
  border: 1px solid rgba(220, 53, 69, 0.25);
`;

export const SuccessMessage = styled.div`
  background: rgba(56, 161, 105, 0.08);
  color: rgba(56, 161, 105, 0.95);
  padding: 0.75rem;
  border-radius: var(--card-radius);
  font-size: 0.875rem;
  border: 1px solid rgba(56, 161, 105, 0.25);
`;

export const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const RequirementsList = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 0.75rem;
  margin-top: 0.5rem;
`;

export const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${props => props.valid ? 'var(--success-color)' : 'var(--text-muted)'};
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const RequirementIcon = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.valid ? 'var(--success-color)' : 'var(--text-muted)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-size: 8px;
  font-weight: bold;
`;
